import {
  FC,
  createElement,
  CSSProperties,
  forwardRef,
  ForwardRefExoticComponent,
  RefAttributes,
  ReactNode,
} from 'react';
import { createUseHtmlId } from '../../hooks/useHtmlId';
import { BaseLabelsProps, WithLabelsProps, WithLabelsBaseProps } from './';
import { Label } from '../Label';
import { Text, TextTokens, TextTokensProps } from '../Text';
import { renderInlineCssVars, useDesignTokens } from '../../theme';
import { postfixTestId } from '../../util/testId';

import cx from 'classnames';
import cn from './withLabels.css.json';
import './withLabels.css';

/**
 * internal props of Labels
 */
interface LabelIdsProps {
  inputId?: string;
  labelId?: string;
  suffixId?: string;
  descriptionId?: string;
}

interface LabelsProps extends Omit<BaseLabelsProps, 'labelId'>, LabelIdsProps {
  children?: ReactNode;
}

const isFieldWidthAPercentage = (fieldWidth: CSSProperties['width']) => {
  if (typeof fieldWidth !== 'string') return false;
  return fieldWidth.includes('%');
};

const Labels = (props: LabelsProps) => {
  const dt = useDesignTokens();

  const fieldWidthAsPercentage = isFieldWidthAPercentage(props.fieldWidth);

  const fauxLabelStyling: TextTokens = (props: TextTokensProps) => ({
    fg: props.disabled ? dt.stateStandardNova.disabled.fg : dt.c.colorTextLabel,
  });

  const style = renderInlineCssVars(
    {
      'field-label-width': props.fieldLabelWidth,
      'field-label-min-width': props.fieldLabelMinWidth,
      'field-width': props.fieldWidth,
      'field-min-width': props.fieldMinWidth,
      'field-suffix-min-width': props.suffix && (props.fieldSuffixMinWidth || '3rem'),
      'spacing-xs': dt.spacing.core.xs,
      'spacing-s': dt.spacing.core.s,
      'spacing-m': dt.spacing.core.m,
      ...(props.labelSpacing?.leftAligned
        ? { 'horizontal-spacing-override': dt.spacing.core[props.labelSpacing.leftAligned] }
        : {}),
      ...(props.labelSpacing?.topAligned
        ? { 'vertical-spacing-override': dt.spacing.core[props.labelSpacing.topAligned] }
        : {}),
      'font-size-s': dt.fontNova.size.s,
    },
    'with-labels'
  );
  return (
    <div
      className={cx(
        props.className,
        cn.withLabels,
        fieldWidthAsPercentage && cn.fieldWidthAsPercentage,
        props.labelPosition === 'left' ? cn.labelLeft : cn.labelTop
      )}
      style={{ ...style, ...props.style }}
      data-testid={props.testId}
    >
      {/*
        render the component first so that focus could move from the component to the label help
        It's not supposed to affect the UI layout since it's determined by the css grid.
      */}
      {props.children && <div className={cn.input}>{props.children}</div>}
      {props.label && (
        <div className={cn.label}>
          <Label
            containerRef={props.labelContainerRef}
            id={props.labelId}
            testId={postfixTestId(props.testId, 'label')}
            className={props.labelClassName}
            style={props.labelStyle}
            disabled={props.disabled}
            required={!props.suppressRequiredIndicator && props.required}
            htmlFor={!props.suppressHtmlFor ? props.inputId : undefined}
            wrapText={props.wrapLabel}
            helpPopup={props.labelHelp}
            highlightableConfig={props.labelHighlightableConfig}
          >
            {props.label}
          </Label>
        </div>
      )}

      {props.suffix && (
        <div className={cn.suffix}>
          <Text
            id={props.suffixId}
            testId={postfixTestId(props.testId, 'suffix')}
            disabled={props.disabled}
            wrapText={false}
            tokens={fauxLabelStyling}
          >
            {props.suffix}
          </Text>
        </div>
      )}
      {props.description && (
        <div className={cn.description}>
          <Text
            id={props.descriptionId}
            testId={postfixTestId(props.testId, 'description')}
            className={cn.text}
            disabled={props.disabled}
            wrapText={props.wrapDescription}
            tokens={fauxLabelStyling}
          >
            {props.description}
          </Text>
        </div>
      )}
    </div>
  );
};

const useHtmlId = createUseHtmlId('withLabels');

/**
 * A HOC to enable components to have label, description, and suffix.
 * It accepts a Component and returns a new Component which would render a grid containing the label, Component, suffix and description.
 * data-with-labels prop is passed down for components to check if it is used under withLabels.
 *
 * @remarks
 * Text and BaseText are not allowed to be used with withLabels, this may cause the accessibility problem. Please use readonly {@link Input}, {@link Textarea}, {@link Select}, etc. instead of Text or BaseText.
 *
 * @param Comp the Component to be labeled and described.
 * @return A higher order component that supports prop label, description, suffix and so on.
 * @example
 * ```tsx
 * import { withLabels } from '@sas/nova-commons/components/withLabels';
 * import { Input } from '@sas/nova-commons/components/Input';
 *
 * const InputWithLabels = withLabels(Input);
 *
 * export const MyComp = () => {
 *   return (
 *     <InputWithLabels label="label" description="description" value="value" onChange={() => {}} />
 *   )
 * }
 * ```
 */
export function withLabels<T extends WithLabelsBaseProps, R>(
  Comp: FC<T> | ForwardRefExoticComponent<T & RefAttributes<R>>
) {
  const displayName = `${Comp.name}WithLabels`;

  const LabelsWrapper = forwardRef<R, T & WithLabelsProps>(function LabelsWrapper(props, ref) {
    const [id, autoLabelId, suffixId, descriptionId] = useHtmlId(4);
    const inputId = props.id || id;
    const labelProps = props as Omit<BaseLabelsProps, 'className' | 'style'>;
    const {
      className,
      style,
      label,
      labelId: labelIdProp,
      labelClassName,
      labelStyle,
      labelPosition,
      labelHelp,
      labelContainerRef,
      labelHighlightableConfig,
      suffix,
      description,
      wrapLabel,
      wrapDescription,
      suppressHtmlFor,
      suppressRequiredIndicator,
      fieldLabelWidth,
      fieldLabelMinWidth,
      fieldMinWidth,
      fieldWidth,
      fieldSuffixMinWidth,
      labelSpacing,
      labelingAttr,
      inputStyle,
      inputClassName,
      ariaLabel: originalAriaLabel,
      ariaLabelledBy: originalAriaLabelledBy,
      ariaDescribedBy: originalAriaDescribedBy,
      ...compProps
    } = props; // some components are going to set {...props} to a DOM so we should give the exact comp props (get rid of WithLabelProps except required and testId)
    const fieldWidthAsPercentage = isFieldWidthAPercentage(fieldWidth);
    const labelId = labelIdProp ?? autoLabelId;
    /*
     * aria-label would override the htmlFor but be overridden by aria-labelledy
     * htmlFor association supports a focus feature natively that the focus would move to the component when click the label
     * Some components have an inner aria-label. So withLabels need to prepend the label to the aria-label
     * Some components also allow customized aria-labelledby. So withLabels need to prepend the label id to the aria-labelledby
     */
    const ariaLabel =
      labelingAttr === 'aria-label' || (labelingAttr === undefined && !!originalAriaLabel)
        ? [label, originalAriaLabel].filter((label) => !!label).join(', ')
        : undefined;
    const ariaLabelledBy =
      labelingAttr === 'aria-labelledby' || (labelingAttr === undefined && !ariaLabel)
        ? [labelId].concat(originalAriaLabelledBy ?? []).join(' ')
        : undefined;
    let ariaDescribedBy: string[] = [];
    if (suffix) ariaDescribedBy.push(suffixId);
    if (description) ariaDescribedBy.push(descriptionId);
    if (originalAriaDescribedBy) ariaDescribedBy = ariaDescribedBy.concat(originalAriaDescribedBy);
    const ariaProps: Pick<
      WithLabelsBaseProps,
      'ariaLabel' | 'ariaLabelledBy' | 'ariaDescribedBy'
    > = {};
    if (ariaLabel !== undefined) ariaProps.ariaLabel = ariaLabel;
    if (ariaLabelledBy !== undefined) ariaProps.ariaLabelledBy = ariaLabelledBy;
    if (ariaDescribedBy.length > 0) ariaProps.ariaDescribedBy = [ariaDescribedBy.join(' ')];
    // there are two types, string or string[], used for ariaDescribedBy in different components,
    // if the string is the type of ariaDescribedBy and the ['abc', 'def'] is passed in, it will be converted to "abc,def", it is incorrect.
    // so concatenate all of elemens and construct a new array with only one element.

    return (
      <Labels
        className={displayName + (className ? ` ${className}` : '')}
        style={style}
        {...labelProps}
        suppressRequiredIndicator={
          suppressRequiredIndicator || props.readOnly || props.editable === false
        }
        inputId={
          labelingAttr === 'htmlFor' || (labelingAttr === undefined && !ariaLabel)
            ? inputId
            : undefined
        }
        labelId={labelId}
        suffixId={suffixId}
        descriptionId={descriptionId}
        testId={postfixTestId(props.testId, 'withLabels')}
      >
        <Comp
          {...(compProps as T)}
          id={inputId}
          ref={ref}
          className={inputClassName}
          /**
           * data-* attributes are automatically forwarded to components and are safely rendered to the DOM.
           * This allows us to pass this prop freely to any component used by withLabels and components that
           * require this feature can implement the API independently as needed.
           */
          data-with-labels
          style={{
            maxWidth: '100%',
            ...inputStyle,
            width: fieldWidthAsPercentage ? '100%' : fieldWidth,
            minWidth: fieldMinWidth,
          }}
          {...ariaProps}
        />
      </Labels>
    );
    // Not sure why but the type check does not work for <Comp />. It could accept ref and aria properties that it may not support.
  });
  LabelsWrapper.displayName = displayName;
  return LabelsWrapper;
}
